import { z } from 'zod';
import { ReactNode } from 'react';

export interface Experience {
  currentExp: number;
  level: number;
  nextLevelExp: number;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: ReactNode;
  progress: number;
  target: number;
}

export interface Challenge {
  id: string;
  title: string;
  description: string;
  type: 'daily' | 'weekly';
  target: number;
  reward: {
    experience: number;
    badge?: string;
  };
  progress: number;
  endDate: Date;
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  type: 'pomodoros' | 'focus' | 'tasks' | 'streak';
  target: number;
  progress: number;
  reward: {
    experience: number;
    badge?: string;
  };
  completed: boolean;
}

export const EXPERIENCE_PER_LEVEL = 1000; // Base XP needed per level
export const XP_MULTIPLIER = 1.5; // XP requirement increases by this factor each level

export const calculateLevel = (totalExp: number): Experience => {
  let level = 1;
  let expForNextLevel = EXPERIENCE_PER_LEVEL;
  let remainingExp = totalExp;

  while (remainingExp >= expForNextLevel) {
    remainingExp -= expForNextLevel;
    level++;
    expForNextLevel = Math.floor(EXPERIENCE_PER_LEVEL * Math.pow(XP_MULTIPLIER, level - 1));
  }

  return {
    currentExp: remainingExp,
    level,
    nextLevelExp: expForNextLevel,
  };
};

// XP rewards for different actions
export const XP_REWARDS = {
  POMODORO_COMPLETE: 50,
  TASK_COMPLETE: 30,
  DAILY_STREAK: 100,
  WEEKLY_GOAL: 200,
  CHALLENGE_COMPLETE: 500,
} as const;

// Achievement definitions
export const ACHIEVEMENTS = {
  // Milestone achievements (total pomodoros)
  FIRST_POMODORO: { id: 'FIRST_POMODORO', title: 'First Focus', description: 'Complete your first pomodoro', icon: '🎯', maxProgress: 1, xp: 100 },
  FIVE_SESSIONS: { id: 'FIVE_SESSIONS', title: 'Getting Started', description: 'Complete 5 pomodoros', icon: '🌱', maxProgress: 5, xp: 150 },
  TEN_SESSIONS: { id: 'TEN_SESSIONS', title: 'Double Digits', description: 'Complete 10 pomodoros', icon: '✨', maxProgress: 10, xp: 200 },
  TWENTY_FIVE_SESSIONS: { id: 'TWENTY_FIVE_SESSIONS', title: 'Quarter Century', description: 'Complete 25 pomodoros', icon: '🏅', maxProgress: 25, xp: 400 },
  FIFTY_SESSIONS: { id: 'FIFTY_SESSIONS', title: 'Half Century', description: 'Complete 50 pomodoros', icon: '🥈', maxProgress: 50, xp: 600 },
  PRODUCTIVITY_MASTER: { id: 'PRODUCTIVITY_MASTER', title: 'Productivity Master', description: 'Complete 100 pomodoros', icon: '👑', maxProgress: 100, xp: 1000 },
  TWO_FIFTY_SESSIONS: { id: 'TWO_FIFTY_SESSIONS', title: 'Power User', description: 'Complete 250 pomodoros', icon: '💎', maxProgress: 250, xp: 2500 },
  FIVE_HUNDRED_SESSIONS: { id: 'FIVE_HUNDRED_SESSIONS', title: 'Pomodoro Legend', description: 'Complete 500 pomodoros', icon: '🏆', maxProgress: 500, xp: 5000 },
  THOUSAND_SESSIONS: { id: 'THOUSAND_SESSIONS', title: 'Unstoppable', description: 'Complete 1000 pomodoros', icon: '⚡', maxProgress: 1000, xp: 10000 },

  // Time-based achievements (total focus hours)
  ONE_HOUR_TOTAL: { id: 'ONE_HOUR_TOTAL', title: 'First Hour', description: 'Accumulate 1 hour of focus time', icon: '⏱️', maxProgress: 60, xp: 100 },
  TEN_HOURS_TOTAL: { id: 'TEN_HOURS_TOTAL', title: 'Dedicated', description: 'Accumulate 10 hours of focus time', icon: '⏰', maxProgress: 600, xp: 500 },
  FIFTY_HOURS_TOTAL: { id: 'FIFTY_HOURS_TOTAL', title: 'Committed', description: 'Accumulate 50 hours of focus time', icon: '🕐', maxProgress: 3000, xp: 1500 },
  HUNDRED_HOURS_TOTAL: { id: 'HUNDRED_HOURS_TOTAL', title: 'Centurion', description: 'Accumulate 100 hours of focus time', icon: '🕰️', maxProgress: 6000, xp: 3000 },
  FIVE_HUNDRED_HOURS: { id: 'FIVE_HUNDRED_HOURS', title: 'Marathon Runner', description: 'Accumulate 500 hours of focus time', icon: '🏃', maxProgress: 30000, xp: 8000 },

  // Time-of-day achievements
  EARLY_BIRD: { id: 'EARLY_BIRD', title: 'Early Bird', description: 'Complete 5 pomodoros before 9 AM', icon: '🌅', maxProgress: 5, xp: 300 },
  NIGHT_OWL: { id: 'NIGHT_OWL', title: 'Night Owl', description: 'Complete 5 pomodoros after 8 PM', icon: '🦉', maxProgress: 5, xp: 300 },

  // Streak achievements
  THREE_DAY_STREAK: { id: 'THREE_DAY_STREAK', title: 'Momentum', description: 'Maintain a 3-day streak', icon: '🔗', maxProgress: 3, xp: 200 },
  STREAK_MASTER: { id: 'STREAK_MASTER', title: 'Streak Master', description: 'Maintain a 7-day streak', icon: '🔥', maxProgress: 7, xp: 500 },
  FOURTEEN_DAY_STREAK: { id: 'FOURTEEN_DAY_STREAK', title: 'Two Weeks Strong', description: 'Maintain a 14-day streak', icon: '💪', maxProgress: 14, xp: 1000 },
  MONTHLY_MASTER: { id: 'MONTHLY_MASTER', title: 'Monthly Master', description: 'Maintain a 30-day streak', icon: '📅', maxProgress: 30, xp: 2000 },
  SIXTY_DAY_STREAK: { id: 'SIXTY_DAY_STREAK', title: 'Habit Formed', description: 'Maintain a 60-day streak', icon: '🧠', maxProgress: 60, xp: 5000 },
  HUNDRED_DAY_STREAK: { id: 'HUNDRED_DAY_STREAK', title: 'Centurion Streak', description: 'Maintain a 100-day streak', icon: '💯', maxProgress: 100, xp: 10000 },

  // Session length achievements
  LONG_SESSION: { id: 'LONG_SESSION', title: 'Deep Focus', description: 'Complete a 45+ minute session', icon: '🧘', maxProgress: 1, xp: 200 },
  MARATHON_SESSION: { id: 'MARATHON_SESSION', title: 'Marathon Focus', description: 'Complete a 90+ minute session', icon: '🏋️', maxProgress: 1, xp: 500 },

  // Daily volume achievements
  FIVE_IN_A_DAY: { id: 'FIVE_IN_A_DAY', title: 'Productive Day', description: 'Complete 5 pomodoros in one day', icon: '📈', maxProgress: 1, xp: 300 },
  TEN_IN_A_DAY: { id: 'TEN_IN_A_DAY', title: 'Super Productive', description: 'Complete 10 pomodoros in one day', icon: '🚀', maxProgress: 1, xp: 800 },
  WEEKEND_WARRIOR: { id: 'WEEKEND_WARRIOR', title: 'Weekend Warrior', description: 'Complete pomodoros on both Saturday and Sunday', icon: '🗓️', maxProgress: 1, xp: 400 },

  // Project achievements
  FIRST_PROJECT: { id: 'FIRST_PROJECT', title: 'Project Starter', description: 'Complete a pomodoro with a project assigned', icon: '📁', maxProgress: 1, xp: 100 },
  MULTI_PROJECT: { id: 'MULTI_PROJECT', title: 'Multi-Tasker', description: 'Work on 3 different projects', icon: '🗂️', maxProgress: 3, xp: 300 },

  // Learn module achievements
  FIRST_VIDEO: { id: 'FIRST_VIDEO', title: 'First Lesson', description: 'Analyze your first video', icon: '🎬', maxProgress: 1, xp: 100 },
  TEN_VIDEOS: { id: 'TEN_VIDEOS', title: 'Lifelong Learner', description: 'Analyze 10 videos', icon: '📚', maxProgress: 10, xp: 500 },
  FIFTY_VIDEOS: { id: 'FIFTY_VIDEOS', title: 'Knowledge Seeker', description: 'Analyze 50 videos', icon: '🧠', maxProgress: 50, xp: 2000 },
  CURIOUS_MIND: { id: 'CURIOUS_MIND', title: 'Curious Mind', description: 'Ask 25 questions', icon: '❓', maxProgress: 25, xp: 400 },
  HUNDRED_QUESTIONS: { id: 'HUNDRED_QUESTIONS', title: 'Inquisitor', description: 'Ask 100 questions', icon: '🔍', maxProgress: 100, xp: 1500 },
  SPACED_REPETITION: { id: 'SPACED_REPETITION', title: 'Total Recall', description: 'Review 50 flashcards', icon: '🃏', maxProgress: 50, xp: 600 },
  RETENTION_MASTER: { id: 'RETENTION_MASTER', title: 'Retention Master', description: 'Review 250 flashcards', icon: '🎓', maxProgress: 250, xp: 2500 },
  LONG_LECTURE: { id: 'LONG_LECTURE', title: 'Marathon Watcher', description: 'Analyze a video over 1 hour', icon: '⏳', maxProgress: 1, xp: 300 },
} as const;

export interface Challenge {
  id: string;
  title: string;
  description: string;
  type: 'daily' | 'weekly';
  target: number;
  reward: {
    experience: number;
    badge?: string;
  };
  progress: number;
  endDate: Date;
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  type: 'pomodoros' | 'focus' | 'tasks' | 'streak';
  target: number;
  progress: number;
  reward: {
    experience: number;
    badge?: string;
  };
  completed: boolean;
}

export const LEVEL_REWARDS = {
  2: {
    title: 'Beginner Timer',
    feature: 'Custom timer durations',
    badge: '⏱️',
  },
  5: {
    title: 'Focus Warrior',
    feature: 'Custom sound themes',
    badge: '🎵',
  },
  10: {
    title: 'Productivity Master',
    feature: 'Advanced statistics',
    badge: '📊',
  },
  15: {
    title: 'Time Lord',
    feature: 'Create custom challenges',
    badge: '⚔️',
  },
  20: {
    title: 'Grandmaster',
    feature: 'Custom dashboard layouts',
    badge: '🏆',
  },
  25: {
    title: 'Focus Sage',
    feature: 'AI-powered insights',
    badge: '🧠',
  },
  30: {
    title: 'Time Bender',
    feature: 'Advanced project templates',
    badge: '🌀',
  },
  40: {
    title: 'Productivity Legend',
    feature: 'Custom automation rules',
    badge: '⚡',
  },
  50: {
    title: 'Time Architect',
    feature: 'Team collaboration tools',
    badge: '👥',
  },
  75: {
    title: 'Focus Emperor',
    feature: 'Priority AI assistant',
    badge: '👑',
  },
  100: {
    title: 'Time God',
    feature: 'Create custom themes',
    badge: '✨',
  }
} as const;